/**
 * AddSubLoginDialog — create a tenant sub-login with module-level ACL.
 *
 * Two-step UX so the user always sees what they're granting:
 *   1. On open, GET /api/revqdemo/sub-logins/modules → render module checkboxes.
 *   2. User fills name + email + password, ticks modules, submits →
 *      POST /api/revqdemo/sub-logins with { ..., allowed_modules: [keys] }.
 *
 * Primary-token only — the parent (RevqdemoMetricsPanel) hides the trigger
 * when the active JWT is a sub-user token (vendor returns 403 on GET).
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { getSubLoginModules, createSubLogin } from '../../lib/api/ediApi';

function readVendorError(data, fallback) {
  const d = data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d.length) {
    const msgs = d.map((e) => e?.msg || e?.message).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (d && typeof d === 'object') {
    const issues = Array.isArray(d.issues) ? d.issues.filter(Boolean) : [];
    if (d.message && issues.length) return `${d.message}: ${issues.join('; ')}`;
    if (d.message) return d.message;
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}

const AddSubLoginDialog = ({ token, onClose, onAdded }) => {
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [modulesError, setModulesError] = useState('');

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [selected, setSelected] = useState(new Set());

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setModulesLoading(true);
      try {
        const { ok, status, data } = await getSubLoginModules(token);
        if (cancelled) return;
        if (!ok) {
          setModulesError(readVendorError(data, `Modules fetch failed (HTTP ${status})`));
          return;
        }
        setModules(Array.isArray(data?.modules) ? data.modules : []);
      } catch (err) {
        if (!cancelled) setModulesError(err.message || 'Modules fetch failed');
      } finally {
        if (!cancelled) setModulesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const toggleModule = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(modules.map((m) => m.key)));
  const clearAll = () => setSelected(new Set());

  const handleField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.first_name.trim() &&
    form.last_name.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.password.length >= 8 &&
    !saving;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Fill name, a valid email, and an 8+ char password.');
      return;
    }
    setSaving(true);
    try {
      const { ok, status, data } = await createSubLogin(token, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        allowed_modules: Array.from(selected),
      });
      if (!ok) {
        setError(readVendorError(data, `Create failed (HTTP ${status})`));
        return;
      }
      setSuccess(true);
      onAdded?.();
      setTimeout(() => { onClose?.(); }, 1000);
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      data-testid="add-sublogin-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-900">Create sub-login</p>
          <button
            onClick={() => { if (!saving) onClose?.(); }}
            disabled={saving}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30"
            data-testid="add-sublogin-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {/* Name + email + password */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[12px] text-slate-600 font-medium">First name</label>
              <Input
                value={form.first_name}
                onChange={(e) => handleField('first_name', e.target.value)}
                placeholder="Jane"
                disabled={saving || success}
                className="text-sm"
                data-testid="add-sublogin-first-name"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] text-slate-600 font-medium">Last name</label>
              <Input
                value={form.last_name}
                onChange={(e) => handleField('last_name', e.target.value)}
                placeholder="Doe"
                disabled={saving || success}
                className="text-sm"
                data-testid="add-sublogin-last-name"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[12px] text-slate-600 font-medium">Email</label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => handleField('email', e.target.value)}
              placeholder="jane.doe@example.com"
              disabled={saving || success}
              className="text-sm"
              data-testid="add-sublogin-email"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[12px] text-slate-600 font-medium">
              Temporary password
              <span className="ml-1 text-slate-400 font-normal">(sub-user is forced to change on first login)</span>
            </label>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => handleField('password', e.target.value)}
              placeholder="At least 8 characters"
              disabled={saving || success}
              className="text-sm font-mono"
              data-testid="add-sublogin-password"
            />
          </div>

          {/* Module ACL */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[12px] text-slate-600 font-medium">
                Allowed modules
                <span className="ml-1 text-slate-400 font-normal">
                  ({selected.size} selected)
                </span>
              </label>
              <div className="flex items-center gap-2 text-[10px]">
                <button
                  type="button"
                  onClick={selectAll}
                  disabled={modulesLoading || saving || success || modules.length === 0}
                  className="text-slate-600 hover:text-slate-900 underline disabled:opacity-30"
                  data-testid="add-sublogin-modules-all"
                >
                  Select all
                </button>
                <span className="text-slate-300">·</span>
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={modulesLoading || saving || success || selected.size === 0}
                  className="text-slate-600 hover:text-slate-900 underline disabled:opacity-30"
                  data-testid="add-sublogin-modules-clear"
                >
                  Clear
                </button>
              </div>
            </div>

            {modulesLoading && (
              <div className="flex items-center gap-2 text-[11px] text-slate-500" data-testid="add-sublogin-modules-loading">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading modules…
              </div>
            )}

            {modulesError && !modulesLoading && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{modulesError}</span>
              </div>
            )}

            {!modulesLoading && !modulesError && (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto rounded-lg border border-slate-200 p-2 bg-slate-50"
                data-testid="add-sublogin-modules-list"
              >
                {modules.map((m) => {
                  const checked = selected.has(m.key);
                  return (
                    <label
                      key={m.key}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-[11px] transition-colors ${
                        checked ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-transparent hover:bg-white'
                      }`}
                      data-testid={`add-sublogin-module-${m.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModule(m.key)}
                        disabled={saving || success}
                        className="h-3 w-3 accent-emerald-600"
                      />
                      <span className="text-slate-700 truncate">{m.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-slate-400">POST /api/edi/sub-logins · allowed_modules ACL</p>
          </div>

          {success && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-700" data-testid="add-sublogin-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Sub-login created. They must change the password on first login.
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] text-red-700" data-testid="add-sublogin-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!success && (
            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-slate-900 hover:bg-slate-800 gap-1.5"
              data-testid="add-sublogin-submit"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Create sub-login
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddSubLoginDialog;
